import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Check, Lock } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { CalmTry } from "@/components/landing/CalmTry";
import { WhatsNew } from "@/components/landing/WhatsNew";
import { PlaybookShowcase } from "@/components/landing/PlaybookShowcase";
import { Icon } from "@/components/Icon";
import { FEATURES, FAQ_ITEMS } from "@/data/landing";
import { usePageMeta } from "@/hooks/usePageMeta";

const HERO_IMG = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=70";

const Hero = () => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 grid lg:grid-cols-2 gap-12 items-center">
    <div className="mm-stagger">
      <span className="mm-chip !cursor-default !text-xs">✨ Lifetime access · No subscription</span>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 leading-[1.05]">
        One app for your whole <span className="text-mm-primary">mental health</span>
      </h1>
      <p className="text-mm-muted mt-5 text-base sm:text-lg max-w-lg">
        The app that learns what actually helps you. Check in, try 100+ coping skills, and get a personal playbook that Mia, your AI companion, remembers on hard days.
      </p>
      <div className="flex flex-wrap gap-3 mt-8">
        <Link to="/checkout" className="mm-btn" data-testid="landing-get-started-button">Get Started — $19.99</Link>
        <a href="#features" className="mm-btn-ghost" data-testid="landing-see-inside-link">See what's inside first</a>
        <Link to="/tools" className="mm-btn-ghost" data-testid="landing-explore-tools">Explore free calm tools</Link>
      </div>
      <Link to="/wellbeing-check" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-mm-primary hover:underline" data-testid="landing-wellbeing-link">Not sure how you're doing? Take the free 2-minute wellbeing check →</Link>
      <div className="flex items-center gap-2 text-sm text-mm-muted mt-4"><Lock size={14} /> Your data stays yours. Export or delete anytime.</div>
    </div>
    <div className="relative mm-fade-up">
      <img src={HERO_IMG} alt="Calm meditation" className="rounded-[32px] w-full h-[420px] object-cover" />
      <div className="absolute -bottom-6 -left-4 mm-card p-4 shadow-xl w-60">
        <div className="text-xs text-mm-muted font-semibold uppercase tracking-wider">Pattern Found</div>
        <div className="font-semibold mt-1">Mood is 2.1 pts higher with 6+ hrs sleep</div>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="max-w-6xl mx-auto px-5 sm:px-8 mt-28">
    <h2 className="text-3xl sm:text-4xl font-bold">Everything in one place</h2>
    <p className="text-mm-muted mt-2">No more app-hopping. Just one place to check in and figure out what actually helps.</p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 mm-stagger">
      {FEATURES.map((f) => (
        <div key={f.id} className="mm-card mm-card-hover p-6" data-testid={`landing-feature-${f.id}`}>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ background: `linear-gradient(135deg, ${f.gradient?.[0] || f.color}, ${f.gradient?.[1] || f.color})` }}>
            <Icon name={f.icon} />
          </div>
          <h3 className="font-semibold text-lg mt-4">{f.title}</h3>
          <p className="text-mm-muted text-sm mt-2 leading-relaxed">{f.description}</p>
        </div>
      ))}
    </div>
  </section>
);

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-3xl mx-auto px-5 sm:px-8 mt-28">
      <h2 className="text-3xl font-bold text-center">Common questions</h2>
      <div className="mt-8 space-y-3">
        {FAQ_ITEMS.map((q, i) => (
          <button key={q.question} onClick={() => setOpen(open === i ? -1 : i)} data-testid={`landing-faq-${i}`} className="mm-card w-full text-left p-5">
            <div className="flex justify-between gap-4 font-semibold">{q.question}<ChevronDown size={18} className={`transition-transform ${open === i ? "rotate-180" : ""}`} /></div>
            {open === i && <p className="text-mm-muted text-sm mt-3 leading-relaxed">{q.answer}</p>}
          </button>
        ))}
      </div>
    </section>
  );
};

export default function Landing() {
  usePageMeta({ title: "Mindful Me — One app for your whole mental health", description: "A personal \"What Helps Me\" playbook learned from your own check-ins, plus voice journaling, a mood garden, Open-When letters, 100+ coping skills, an AI companion and a free wellbeing check. One payment, lifetime access.", path: "/landing" });
  return (
    <PublicLayout>
      <Hero />
      <CalmTry />
      <PlaybookShowcase />
      <Features />
      <WhatsNew />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-28">
        <div className="mm-gradient rounded-[32px] p-10 sm:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold">Your data stays yours</h2>
            <p className="opacity-90 mt-3">Encrypted, never sold, never used to train AI. Export to CSV or wipe it with one tap.</p>
          </div>
          <ul className="space-y-3">
            {["No ads, no data selling", "AI chats never train models", "Export or delete anytime", "Share only what you choose with supporters"].map((t) => (
              <li key={t} className="flex items-center gap-3"><Check size={18} /> {t}</li>
            ))}
          </ul>
        </div>
      </section>
      <Faq />
      <section className="text-center mt-24 px-5">
        <h2 className="text-3xl font-bold">Ready to see what triggers your hard days?</h2>
        <Link to="/checkout" className="mm-btn mt-6" data-testid="landing-bottom-cta">Get Started — $19.99</Link>
      </section>
    </PublicLayout>
  );
}
